// TMS Design System Tokens
// Central export for all design tokens

export * from './colors';
export * from './typography';
export * from './spacing';
export * from './shadows';

// Import and re-export specific tokens for convenience
import { colors, cssVariables as colorVariables } from './colors';
import { typography, typographyVariables, textStyles } from './typography';
import { spacing, spacingVariables, borderRadius, zIndex } from './spacing';
import { shadows, shadowVariables, createShadow, combineShadows } from './shadows';

export {
  colors,
  colorVariables,
  typography,
  typographyVariables,
  textStyles,
  spacing,
  spacingVariables,
  borderRadius,
  zIndex,
  shadows,
  shadowVariables,
  createShadow,
  combineShadows,
};

// Combined CSS variables for easy injection
export const allCssVariables = {
  ...colorVariables,
  ...typographyVariables,
  ...spacingVariables,
  ...shadowVariables,
} as const;

// Design token collections for theme generation
export const designTokens = {
  colors,
  typography,
  spacing,
  shadows,
  borderRadius,
  zIndex,
} as const;

// Breakpoints for responsive design
export const breakpoints = {
  xs: '320px', // Mobile (small)
  sm: '640px', // Mobile (large)
  md: '768px', // Tablet
  lg: '1024px', // Desktop (small)
  xl: '1280px', // Desktop (large)
  '2xl': '1536px', // Desktop (extra large)
  '3xl': '1920px', // 4K displays
} as const;

// Animation durations and easings
export const animations = {
  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
    slower: '750ms',
    slowest: '1000ms',
  },
  easing: {
    linear: 'linear',
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',

    // Custom cubic-bezier curves
    bounceIn: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    bounceOut: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
} as const;

// Component variants for styling
export const componentVariants = {
  size: ['xs', 'sm', 'md', 'lg', 'xl'] as const,
  color: ['primary', 'secondary', 'success', 'warning', 'danger', 'info'] as const,
  variant: ['solid', 'outline', 'ghost', 'link'] as const,
  radius: ['none', 'sm', 'md', 'lg', 'xl', 'full'] as const,
} as const;

// Type definitions for all tokens
export type BreakpointKey = keyof typeof breakpoints;
export type AnimationDuration = keyof typeof animations.duration;
export type AnimationEasing = keyof typeof animations.easing;
export type ComponentSize = (typeof componentVariants.size)[number];
export type ComponentColor = (typeof componentVariants.color)[number];
export type ComponentVariant = (typeof componentVariants.variant)[number];
export type ComponentRadius = (typeof componentVariants.radius)[number];

// Theme configuration type
export interface ThemeConfig {
  colors: typeof colors;
  typography: typeof typography;
  spacing: typeof spacing;
  shadows: typeof shadows;
  borderRadius: typeof borderRadius;
  zIndex: typeof zIndex;
  breakpoints: typeof breakpoints;
  animations: typeof animations;
}
